import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { supabase } from '../../../shared/lib/supabase'

function InviteVerify() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const [email, setEmail] = useState(searchParams.get('email') || '')
  const [code, setCode] = useState('')
  const [loading, setLoading] = useState(false)
  const [resending, setResending] = useState(false)
  const [error, setError] = useState('')
  const [resentMessage, setResentMessage] = useState('')
  const [expiredState, setExpiredState] = useState(false)

  // If already signed in, redirect to app (onboarding guard handles password/MFA)
  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) navigate('/', { replace: true })
    })
  }, [navigate])

  const handleVerify = async (e) => {
    e.preventDefault()
    setError('')
    setResentMessage('')

    const cleanEmail = email.trim().toLowerCase()
    const cleanCode = code.trim().replace(/\s/g, '')

    if (!cleanEmail) {
      setError('Please enter your email address.')
      return
    }
    if (!/^\d{6,8}$/.test(cleanCode)) {
      setError('Please enter the sign-in code from your email.')
      return
    }

    setLoading(true)
    try {
      const { error: verifyError } = await supabase.auth.verifyOtp({
        email: cleanEmail,
        token: cleanCode,
        type: 'email'
      })
      if (verifyError) {
        const msg = (verifyError.message || '').toLowerCase()
        // Supabase returns "Token has expired or is invalid" for both cases.
        // Treat that and obvious expired messages as expired.
        if (msg.includes('expired') || msg.includes('invalid token') || msg.includes('token has expired')) {
          setExpiredState(true)
        } else if (msg.includes('invalid')) {
          setError('Invalid code. Please check the code and try again.')
        } else {
          setError(verifyError.message || 'Could not verify code.')
        }
        return
      }
      // Success — onboarding guard will redirect to password change + MFA enrollment
      navigate('/', { replace: true })
    } catch (err) {
      setError(err.message || 'Verification failed.')
    } finally {
      setLoading(false)
    }
  }

  const handleResend = async () => {
    setError('')
    setResentMessage('')

    const cleanEmail = email.trim().toLowerCase()
    if (!cleanEmail) {
      setError('Please enter your email address to resend the code.')
      return
    }

    setResending(true)
    try {
      const { error: resendError } = await supabase.auth.signInWithOtp({
        email: cleanEmail,
        options: { shouldCreateUser: false }
      })
      if (resendError) {
        setError(resendError.message || 'Could not resend code.')
        return
      }
      setResentMessage('A new code has been sent to your email.')
      setExpiredState(false)
      setCode('')
    } catch (err) {
      setError(err.message || 'Could not resend code.')
    } finally {
      setResending(false)
    }
  }

  // Expired OTP error state — prominent display instead of inline error
  if (expiredState) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center px-4" style={{ backgroundColor: '#012438' }}>
        <img src="/logo.png" alt="SSA Logo" className="w-16 h-16 rounded-2xl object-contain mb-6" />

        <div className="w-full max-w-md bg-white dark:bg-gray-800 rounded-xl shadow-xl p-8">
          <div className="flex flex-col items-center text-center mb-6">
            <div className="w-14 h-14 rounded-full bg-red-100 dark:bg-red-900/30 flex items-center justify-center mb-4">
              <svg className="w-7 h-7 text-red-600 dark:text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white mb-2">Sign-in Code Expired</h1>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              The sign-in code you entered has expired or is no longer valid. Sign-in codes expire after 60 minutes.
            </p>
          </div>

          {resentMessage && (
            <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-green-700 dark:text-green-300 text-sm text-center">
              {resentMessage}
            </div>
          )}

          {error && (
            <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 text-sm">
              {error}
            </div>
          )}

          <div className="space-y-3">
            <button
              onClick={handleResend}
              disabled={resending || !email.trim()}
              className="w-full btn-primary disabled:opacity-50 justify-center"
            >
              {resending ? 'Sending...' : 'Request a new code'}
            </button>
            <button
              onClick={() => { setExpiredState(false); setCode(''); setError(''); setResentMessage('') }}
              className="w-full px-4 py-2.5 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              Enter a different code
            </button>
          </div>

          <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700 text-center">
            <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">
              If you're unable to receive a new code or continue to have issues, please contact your platform administrator for assistance.
            </p>
          </div>
        </div>

        <p className="text-xs mt-6" style={{ color: '#7E8F9C' }}>
          Need help? Contact the administrator who invited you.
        </p>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center px-4" style={{ backgroundColor: '#012438' }}>
      <img src="/logo.png" alt="SSA Logo" className="w-16 h-16 rounded-2xl object-contain mb-6" />

      <div className="w-full max-w-md bg-white dark:bg-gray-800 rounded-xl shadow-xl p-8">
        <h1 className="text-xl font-bold text-gray-900 dark:text-white mb-1">Welcome to Sidewalk Security</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
          Enter the sign-in code sent to your email to sign in. You will be prompted to set a password and enroll in MFA on your first login.
        </p>

        {error && (
          <div className="mb-4 p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300 text-sm">
            {error}
          </div>
        )}

        {resentMessage && (
          <div className="mb-4 p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg text-green-700 dark:text-green-300 text-sm">
            {resentMessage}
          </div>
        )}

        <form onSubmit={handleVerify} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Email Address
            </label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent"
              placeholder="you@company.com"
              autoComplete="email"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Sign-in Code
            </label>
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={8}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/[^0-9]/g, ''))}
              className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary-500 focus:border-transparent font-mono text-lg tracking-widest text-center"
              placeholder="123456"
              autoFocus={!!email}
              required
            />
          </div>

          <button
            type="submit"
            disabled={loading || code.length < 6 || !email.trim()}
            className="w-full btn-primary disabled:opacity-50 justify-center"
          >
            {loading ? 'Verifying...' : 'Verify & Sign In'}
          </button>
        </form>

        <div className="mt-5 pt-5 border-t border-gray-200 dark:border-gray-700">
          <p className="text-xs text-gray-500 dark:text-gray-400 text-center">
            Didn't receive a code?{' '}
            <button
              type="button"
              onClick={handleResend}
              disabled={resending || !email.trim()}
              className="text-primary-600 dark:text-primary-400 hover:underline disabled:opacity-50 font-medium"
            >
              {resending ? 'Sending...' : 'Send a new code'}
            </button>
          </p>
          <p className="text-xs text-gray-400 dark:text-gray-500 text-center mt-3">
            Already have a password?{' '}
            <Link to="/login" className="text-primary-600 dark:text-primary-400 hover:underline font-medium">
              Sign in here
            </Link>
          </p>
        </div>
      </div>

      <p className="text-xs mt-6" style={{ color: '#7E8F9C' }}>
        Check your spam folder if the code doesn't arrive within 2 minutes.
      </p>
    </div>
  )
}

export default InviteVerify
